import {
  GetChurnRateHistoryQuery,
  GetChurnRateHistoryQueryVariables,
  GetDropoutHistoryQuery,
  GetDropoutHistoryQueryVariables,
  GetRatioHistoryQuery,
  GetRatioHistoryQueryVariables,
  GetRetentionHistoryQuery,
  GetRetentionHistoryQueryVariables,
  GetShowRateHistoryQuery,
  GetShowRateHistoryQueryVariables,
  useGetChurnRateHistoryLazyQuery,
  useGetDropoutHistoryLazyQuery,
  useGetRatioHistoryLazyQuery,
  useGetRetentionHistoryLazyQuery,
  useGetShowRateHistoryLazyQuery,
} from 'gql/types';
import { TFunction } from 'i18next';

import {
  METRIC_HIGHER_IS_BETTER,
  RATIO_BREAKPOINTS,
  STATUS_COLORS,
} from '../../constants';
import { IMetricHistoryConfig } from '../hooks/useMetricHistory';
import {
  IChurnDetailsData,
  IDropoutDetailsData,
  IRatioDetailsData,
  IRetentionDetailsData,
  IShowRateDetailsData,
  IStatusLegendItem,
} from '../types';
import { buildHistoryInput } from '../utils/buildHistoryInput';
import { buildThresholdLegend } from '../utils/buildThresholdLegend';
import { fmtDate } from '../utils/fmtDate';
import { isCurrentPeriod } from '../utils/isCurrentPeriod';

type ChurnPeriod =
  GetChurnRateHistoryQuery['getChurnRateHistory']['periods'][number];
type RetentionPeriod =
  GetRetentionHistoryQuery['getRetentionHistory']['periods'][number];
type DropoutPeriod =
  GetDropoutHistoryQuery['getDropoutHistory']['periods'][number];
type RatioPeriod = GetRatioHistoryQuery['getRatioHistory']['periods'][number];
type ShowRatePeriod =
  GetShowRateHistoryQuery['getShowRateHistory']['periods'][number];

// Ratio shows descriptive (non-threshold) legend labels with no breakpoint values.
const buildRatioLegend = (t: TFunction): IStatusLegendItem[] => [
  { color: STATUS_COLORS.good.fill, label: t('legendGood') },
  { color: STATUS_COLORS.ok.fill, label: t('legendOk') },
  { color: STATUS_COLORS.poor.fill, label: t('legendPoor') },
];

export const churnHistoryConfig: IMetricHistoryConfig<
  GetChurnRateHistoryQuery,
  GetChurnRateHistoryQueryVariables,
  ChurnPeriod,
  IChurnDetailsData
> = {
  useLazyQuery: useGetChurnRateHistoryLazyQuery,
  selectPeriods: (data) => data?.getChurnRateHistory?.periods ?? [],
  buildVariables: ({ filter, periodType, bars, anchorBounds }) => ({
    input: buildHistoryInput(filter, periodType, bars, anchorBounds),
  }),
  getLiveFields: (live) => ({
    churnRate: live.churnRate,
    dropoutCount: live.dropoutCount,
    enrollmentCount: live.enrollmentCount,
    activeAtStart: live.activeAtStart,
  }),
  resolveUseLiveLast: (dateRange, isQuarterly) =>
    isQuarterly || isCurrentPeriod(dateRange),
  getValue: (p) => p.churnRate,
  higherIsBetter: METRIC_HIGHER_IS_BETTER.churn,
  getBreakpoints: (live) => live.breakpoints,
  buildTooltipLines: (p, t) => [
    `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`,
    t('tooltipDropouts', { value: p.dropoutCount }),
    t('tooltipEnrollments', { value: p.enrollmentCount }),
    t('tooltipActiveAtStart', { value: p.activeAtStart }),
  ],
  buildLegend: buildThresholdLegend,
};

export const retentionHistoryConfig: IMetricHistoryConfig<
  GetRetentionHistoryQuery,
  GetRetentionHistoryQueryVariables,
  RetentionPeriod,
  IRetentionDetailsData
> = {
  useLazyQuery: useGetRetentionHistoryLazyQuery,
  selectPeriods: (data) => data?.getRetentionHistory?.periods ?? [],
  buildVariables: ({ filter, periodType, bars, anchorBounds, extra }) => ({
    input: {
      ...buildHistoryInput(filter, periodType, bars, anchorBounds),
      repeatDonationsThreshold: extra.repeatDonationsThreshold ?? undefined,
      historyDataCutoff: extra.historyDataCutoff ?? undefined,
    },
  }),
  getLiveFields: (live) => ({
    retentionScore: live.retentionScore,
    repeatDonorCount: live.repeatDonorCount,
    activeDonorCount: live.activeDonorCount,
  }),
  resolveUseLiveLast: (dateRange) => isCurrentPeriod(dateRange),
  getValue: (p) => p.retentionScore,
  higherIsBetter: METRIC_HIGHER_IS_BETTER.retention,
  getBreakpoints: (live) => live.breakpoints,
  buildTooltipLines: (p, t) => [
    `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`,
    t('tooltipRepeat', { value: p.repeatDonorCount }),
    t('tooltipActive', { value: p.activeDonorCount }),
  ],
  buildLegend: buildThresholdLegend,
};

export const dropoutHistoryConfig: IMetricHistoryConfig<
  GetDropoutHistoryQuery,
  GetDropoutHistoryQueryVariables,
  DropoutPeriod,
  IDropoutDetailsData
> = {
  useLazyQuery: useGetDropoutHistoryLazyQuery,
  selectPeriods: (data) => data?.getDropoutHistory?.periods ?? [],
  buildVariables: ({ filter, periodType, bars, anchorBounds, extra }) => ({
    input: {
      ...buildHistoryInput(filter, periodType, bars, anchorBounds),
      historyDataCutoff: extra.historyDataCutoff ?? undefined,
    },
  }),
  getLiveFields: (live) => ({
    dropoutRate: live.dropoutRate,
    dropoutCount: live.dropoutCount,
    activeAtStart: live.activeAtStart,
  }),
  // Dropout always reflects live card data in the most recent bar.
  resolveUseLiveLast: () => true,
  getValue: (p) => p.dropoutRate,
  higherIsBetter: METRIC_HIGHER_IS_BETTER.dropout,
  getBreakpoints: (live) => live.breakpoints,
  buildTooltipLines: (p, t) => [
    `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`,
    t('tooltipDropouts', { value: p.dropoutCount }),
    t('tooltipActiveAtStart', { value: p.activeAtStart }),
  ],
  buildLegend: buildThresholdLegend,
  // Dropout history anchors on "now" rather than the selected date range.
  getAnchorBounds: () => ({ start: new Date(), end: new Date() }),
};

export const ratioHistoryConfig: IMetricHistoryConfig<
  GetRatioHistoryQuery,
  GetRatioHistoryQueryVariables,
  RatioPeriod,
  IRatioDetailsData
> = {
  useLazyQuery: useGetRatioHistoryLazyQuery,
  selectPeriods: (data) => data?.getRatioHistory?.periods ?? [],
  buildVariables: ({ filter, periodType, bars, anchorBounds, extra }) => ({
    input: {
      ...buildHistoryInput(filter, periodType, bars, anchorBounds),
      repeatDonationsThreshold: extra.repeatDonationsThreshold ?? undefined,
      historyDataCutoff: extra.historyDataCutoff ?? undefined,
    },
  }),
  getLiveFields: (live) => {
    const total = live.firstTimeDonorCount + live.repeatDonorCount;
    return {
      firstTimePct:
        total > 0 ? Math.round((live.firstTimeDonorCount / total) * 100) : 0,
      firstTimeDonorCount: live.firstTimeDonorCount,
      repeatDonorCount: live.repeatDonorCount,
      activeDonorCount: live.activeDonorCount,
    };
  },
  resolveUseLiveLast: (dateRange) => isCurrentPeriod(dateRange),
  getValue: (p) => p.firstTimePct,
  higherIsBetter: METRIC_HIGHER_IS_BETTER.ratio,
  // Ratio uses fixed first-time-percentage breakpoints, not live thresholds.
  getBreakpoints: () => RATIO_BREAKPOINTS,
  buildTooltipLines: (p, t) => [
    `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`,
    t('tooltipFirstTime', { value: p.firstTimeDonorCount }),
    t('tooltipRepeat', { value: p.repeatDonorCount }),
    t('tooltipActiveTotal', { value: p.activeDonorCount }),
  ],
  buildLegend: buildRatioLegend,
};

export const showRateHistoryConfig: IMetricHistoryConfig<
  GetShowRateHistoryQuery,
  GetShowRateHistoryQueryVariables,
  ShowRatePeriod,
  IShowRateDetailsData
> = {
  useLazyQuery: useGetShowRateHistoryLazyQuery,
  selectPeriods: (data) => data?.getShowRateHistory?.periods ?? [],
  buildVariables: ({ filter, periodType, bars, anchorBounds, extra }) => ({
    input: {
      ...buildHistoryInput(filter, periodType, bars, anchorBounds),
      donorDonationFrequency: extra.donorDonationFrequency ?? undefined,
      donorEligibilityDays: extra.donorEligibilityDays ?? undefined,
      historyDataCutoff: extra.historyDataCutoff ?? undefined,
    },
  }),
  getLiveFields: (live) => ({
    showRate: live.showRate,
    donorsWhoDonated: live.donorsWhoDonated,
    expectedDonations: live.expectedDonations,
  }),
  resolveUseLiveLast: (dateRange) => isCurrentPeriod(dateRange),
  getValue: (p) => p.showRate,
  higherIsBetter: METRIC_HIGHER_IS_BETTER.showRate,
  getBreakpoints: (live) => live.breakpoints,
  buildTooltipLines: (p, t) => [
    `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`,
    t('tooltipDonated', { value: p.donorsWhoDonated }),
    t('tooltipExpected', { value: p.expectedDonations }),
  ],
  buildLegend: buildThresholdLegend,
};
